import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { money, pct, statusLabel, statusTone } from "@/lib/format";
import {
  ArrowLeft, Upload, FileText, Loader2, Search, ShieldCheck, CheckCircle2,
  AlertTriangle, XCircle, Download, Sparkles, Users, Home, Landmark,
  Percent as PercentIcon, ListChecks, Gauge, FileCheck2, Trash2, RefreshCw,
  Share2, History, Globe, Copy, ExternalLink
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: Home },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "research", label: "Property Intel", icon: Search },
  { id: "buyer", label: "Buyer", icon: Users },
  { id: "offer", label: "Offer Terms", icon: Landmark },
  { id: "financing", label: "Financing", icon: PercentIcon },
  { id: "commission", label: "Commission", icon: ShieldCheck },
  { id: "missing", label: "Missing Info", icon: ListChecks },
  { id: "readiness", label: "Readiness", icon: Gauge },
  { id: "document", label: "Document", icon: FileCheck2 },
  { id: "audit", label: "Audit", icon: History },
];

export default function TransactionDetail() {
  const { id } = useParams();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/transactions/${id}`);
      setTxn(data);
    } catch (e) {
      toast.error("Transaction not found");
      navigate("/transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading transaction…</div>;
  if (!txn) return null;

  const patch = async (partial) => {
    const { data } = await api.patch(`/transactions/${id}`, partial);
    setTxn(data);
    return data;
  };

  return (
    <div data-testid="txn-detail-page">
      <Link to="/transactions" className="text-xs uppercase tracking-widest text-slate-500 hover:text-brass inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> All transactions
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="kicker mb-2">{txn.transaction_type?.replace(/_/g, " ")}</div>
          <h1 className="text-2xl sm:text-3xl font-display break-words" data-testid="txn-name">{txn.name}</h1>
          <div className="text-sm text-slate-500 font-mono mt-1 break-all">{txn.property_data?.address}</div>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <ShareDialog txnId={txn.id} />
          <span className={`badge-status ${statusTone[txn.status]}`} data-testid="txn-status">
            {statusLabel[txn.status]}
          </span>
        </div>
      </div>

      <div className="grid-panel mt-6">
        <div className="border-b border-border overflow-x-auto">
          <div className="flex min-w-max">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-3 text-sm border-b-2 transition-colors duration-150 flex items-center gap-2 ${
                  tab === t.id
                    ? "border-brass text-navy-900 bg-brass-50/50"
                    : "border-transparent text-slate-500 hover:text-navy hover:bg-slate-50"
                }`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {tab === "overview" && <OverviewTab txn={txn} />}
          {tab === "documents" && <DocumentsTab txn={txn} reload={load} />}
          {tab === "research" && <ResearchTab txn={txn} reload={load} />}
          {tab === "buyer" && <BuyerTab txn={txn} patch={patch} />}
          {tab === "offer" && <OfferTab txn={txn} patch={patch} />}
          {tab === "financing" && <FinancingTab txn={txn} patch={patch} />}
          {tab === "commission" && <CommissionTab txn={txn} patch={patch} reload={load} />}
          {tab === "missing" && <MissingTab txn={txn} reload={load} />}
          {tab === "readiness" && <ReadinessTab txn={txn} reload={load} />}
          {tab === "document" && <DocumentTab txn={txn} reload={load} />}
          {tab === "audit" && <AuditTab txn={txn} />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- SHARE DIALOG ---------------- */
function ShareDialog({ txnId }) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [expiryDays, setExpiryDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/transactions/${txnId}/shares`);
    setShares(data);
  };

  useEffect(() => { if (open) load(); }, [open, txnId]);

  const create = async () => {
    setLoading(true);
    try {
      await api.post(`/transactions/${txnId}/share`, {
        client_email: email || null,
        allow_approval: true,
        expires_in_days: expiryDays === 0 ? null : expiryDays,
      });
      setEmail("");
      await load();
      toast.success(email ? "Share link created — preview email sent" : "Share link created");
    } finally { setLoading(false); }
  };

  const revoke = async (id) => {
    await api.post(`/transactions/${txnId}/shares/${id}/revoke`);
    await load();
    toast.success("Revoked");
  };

  const shareUrl = (t) => `${window.location.origin}/share/${t}`;
  const isExpired = (s) => s.expires_at && new Date(s.expires_at) < new Date();

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-primary text-xs" data-testid="share-btn">
        <Share2 size={12} /> Share
      </button>
      {open && (
        <div className="fixed inset-0 bg-navy/60 z-50 flex items-end sm:items-center justify-center sm:p-8" onClick={() => setOpen(false)}>
          <div className="bg-white border border-border rounded-t-lg sm:rounded-sm w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
            data-testid="share-dialog">
            <div className="border-b border-border px-5 py-3 flex items-center justify-between">
              <div>
                <div className="kicker">Client Portal</div>
                <div className="text-sm font-display text-lg mt-0.5">Share business terms</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-navy" data-testid="close-share">
                <XCircle size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Client email (optional — triggers preview email)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@example.com"
                  data-testid="share-email"
                  className="w-full bg-white border border-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Link expires in</label>
                <div className="inline-flex bg-white border border-border rounded-sm overflow-hidden text-xs" data-testid="expiry-toggle">
                  {[[1, "24h"], [7, "7d"], [30, "30d"], [0, "Never"]].map(([v, l]) => (
                    <button key={v} onClick={() => setExpiryDays(v)} data-testid={`expiry-${v}`}
                      className={`px-3 py-1.5 transition-colors duration-150 ${
                        expiryDays === v ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-50"
                      }`}>{l}</button>
                  ))}
                </div>
              </div>
              <button className="btn-brass w-full justify-center" onClick={create} disabled={loading} data-testid="create-share-btn">
                <Share2 size={12} /> {loading ? "Creating…" : "Create Link"}
              </button>

              <div className="border-t border-border pt-4">
                <div className="kicker mb-2">Active links</div>
                {shares.length === 0 && <div className="text-sm text-slate-500">No links yet.</div>}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {shares.map((s) => {
                    const expired = isExpired(s);
                    return (
                      <div key={s.id} className={`border border-border rounded-sm p-3 text-xs ${s.revoked || expired ? "opacity-50" : ""}`} data-testid={`share-item-${s.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-navy truncate">{s.client_email || "Unattributed link"}</div>
                            <div className="text-slate-500 mt-0.5 truncate font-mono">{shareUrl(s.token)}</div>
                            <div className="text-slate-400 mt-0.5">
                              {s.expires_at ? `Expires ${s.expires_at.slice(0, 10)}` : "Never expires"}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => { navigator.clipboard.writeText(shareUrl(s.token)); toast.success("Copied"); }}
                              className="text-slate-500 hover:text-brass p-1" title="Copy link" data-testid={`copy-share-${s.id}`}>
                              <Copy size={12} />
                            </button>
                            <a href={shareUrl(s.token)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-brass p-1" title="Open">
                              <ExternalLink size={12} />
                            </a>
                            {!s.revoked && (
                              <button onClick={() => revoke(s.id)} className="text-slate-500 hover:text-red-600 p-1" title="Revoke" data-testid={`revoke-share-${s.id}`}>
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                        {s.approved && (
                          <div className="mt-2 text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 size={10} /> Approved by {s.approved_by_name} · {s.approved_at?.slice(0, 10)}
                          </div>
                        )}
                        {s.revoked && <div className="mt-2 text-red-700">Revoked</div>}
                        {!s.revoked && expired && <div className="mt-2 text-amber-700">Expired</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- AUDIT TAB ---------------- */
function AuditTab({ txn }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/transactions/${txn.id}/audit`);
        setEvents(data);
      } finally { setLoading(false); }
    })();
  }, [txn.id]);

  const actionMeta = {
    "transaction.created": ["Transaction created", "text-slate-700"],
    "transaction.updated": ["Transaction updated", "text-slate-700"],
    "document.uploaded": ["Document uploaded", "text-blue-800"],
    "document.deleted": ["Document deleted", "text-red-700"],
    "extraction.completed": ["Extraction ran", "text-blue-800"],
    "research.completed": ["Property research ran", "text-blue-800"],
    "live_research.completed": ["Live public-record research", "text-emerald-700"],
    "fact.confirmed": ["Fact confirmed", "text-emerald-700"],
    "commission.approved": ["Commission approved", "text-brass-700"],
    "document.generated": ["Document generated", "text-brass-700"],
    "final.approved": ["Marked as FINAL", "text-emerald-800"],
    "share.created": ["Share link created", "text-navy"],
    "share.revoked": ["Share link revoked", "text-red-700"],
    "client.approved": ["Client approved business terms", "text-emerald-800"],
  };

  if (loading) return <div className="text-sm text-slate-500">Loading audit trail…</div>;
  if (events.length === 0) return <div className="text-sm text-slate-500">No audit events yet.</div>;

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="kicker">Full audit trail · {events.length} events</div>
      <div className="relative">
        <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
        <div className="space-y-3">
          {events.map((e) => {
            const [label, tone] = actionMeta[e.action] || [e.action.replace(/[._]/g, " "), "text-slate-700"];
            return (
              <div key={e.id} className="relative pl-8" data-testid={`audit-item-${e.id}`}>
                <div className="absolute left-2 top-2 h-2.5 w-2.5 rounded-full bg-brass ring-4 ring-background"></div>
                <div className="grid-panel p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-sm font-medium ${tone}`}>{label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        by <span className="font-medium text-navy">{e.actor_name}</span>
                        {e.entity ? ` · ${e.entity}` : ""}
                      </div>
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 shrink-0">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
                  </div>
                  {e.details && Object.keys(e.details).length > 0 && (
                    <pre className="mt-2 text-[10px] font-mono text-slate-600 bg-slate-50 rounded-sm p-2 overflow-x-auto">
                      {JSON.stringify(e.details, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function OverviewTab({ txn }) {
  const rd = txn.readiness_score;
  const facts = txn.facts || [];
  const confirmed = facts.filter((f) => f.broker_confirmed).length;
  const conflicts = facts.filter((f) => f.classification === "conflicting").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <SummaryRow icon={Home} label="Property" value={txn.property_data?.address} />
        <SummaryRow icon={Users} label="Buyer" value={txn.buyer_data?.buyer_entity || txn.buyer_data?.name} />
        <SummaryRow icon={Landmark} label="Offer Price" value={money(txn.offer_terms?.offer_price)} mono />
        <SummaryRow icon={PercentIcon} label="Commission" value={
          txn.commission_terms?.commission_percentage != null
            ? `${txn.commission_terms.commission_percentage}% · ${money(txn.commission_terms.gross_commission)}`
            : "—"
        } mono />
        <SummaryRow icon={ShieldCheck} label="Commission Approved"
          value={txn.commission_terms?.approved ? "Yes" : "Pending"} />
      </div>

      <div className="space-y-4">
        <div className="grid-panel p-5">
          <div className="kicker mb-2">Readiness</div>
          <div className="font-mono text-4xl text-brass-700" data-testid="overview-readiness">
            {rd?.overall != null ? `${rd.overall}%` : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">{rd?.status?.replace(/_/g, " ")}</div>
        </div>
        <div className="grid-panel p-5">
          <div className="kicker mb-2">Facts</div>
          <div className="flex items-baseline gap-4">
            <div>
              <div className="text-2xl font-mono">{confirmed}<span className="text-slate-400 text-sm">/{facts.length}</span></div>
              <div className="text-xs text-slate-500">Broker-confirmed</div>
            </div>
            <div>
              <div className="text-2xl font-mono text-amber-700">{conflicts}</div>
              <div className="text-xs text-slate-500">Conflicts</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="grid-panel px-5 py-4 flex items-center gap-4">
      <Icon size={16} className="text-brass" />
      <div className="text-xs uppercase tracking-widest text-slate-500 w-40">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} truncate`}>{value || "—"}</div>
    </div>
  );
}

/* ---------------- DOCUMENTS ---------------- */
function DocumentsTab({ txn, reload }) {
  const fileRef = useRef();
  const [kind, setKind] = useState("flyer");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/transactions/${txn.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      await reload();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const extractAll = async () => {
    if (!txn.documents?.length) {
      toast.error("Upload documents first");
      return;
    }
    setExtracting(true);
    try {
      await api.post(`/transactions/${txn.id}/extract`);
      toast.success("Extraction complete — facts added");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); }
  };

  const del = async (docId) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/transactions/${txn.id}/documents/${docId}`);
    reload();
  };

  const KINDS = [
    ["flyer", "Listing Flyer"], ["om", "Offering Memorandum"], ["mls", "MLS / CBA"],
    ["loopnet", "LoopNet / Crexi"], ["email", "Broker Email"], ["rent_roll", "Rent Roll"],
    ["photo", "Property Photo"], ["loi", "Existing LOI"], ["seller_financing", "Seller Financing Proposal"],
    ["title", "Title Report"], ["assessor", "Assessor Record"], ["legal", "Legal Description"],
    ["environmental", "Environmental Report"], ["inspection", "Inspection Report"], ["other", "Other"],
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Document Type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="doc-kind"
              className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass">
              {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Upload File</label>
            <input ref={fileRef} type="file" onChange={upload} disabled={uploading}
              data-testid="doc-file-input"
              accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.jpg,.jpeg,.png"
              className="w-full text-sm file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:text-xs file:uppercase file:tracking-widest file:bg-navy file:text-white hover:file:bg-navy-800" />
          </div>
        </div>
        <button className="btn-brass" onClick={extractAll} disabled={extracting || !txn.documents?.length} data-testid="extract-btn">
          {extracting ? <><Loader2 size={14} className="animate-spin" /> Extracting…</> : <><Sparkles size={14} /> Extract & Enrich</>}
        </button>
      </div>

      <div className="grid-panel">
        <div className="border-b border-border px-4 py-3">
          <div className="kicker">Uploaded ({txn.documents?.length || 0})</div>
        </div>
        {txn.documents?.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 text-center">
            <Upload size={24} className="mx-auto text-slate-300 mb-2" />
            No documents yet. Upload PDFs, DOCX, or spreadsheets to feed extraction.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2">File</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2 text-right">Size</th>
                <th className="px-4 py-2 text-right">Text</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {txn.documents.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2"><FileText size={14} className="text-slate-400" />{d.filename}</div>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{d.kind}</td>
                  <td className="px-4 py-3 mono-num text-xs">{(d.size_bytes / 1024).toFixed(1)} KB</td>
                  <td className="px-4 py-3 text-right">
                    {d.extracted_text
                      ? <span className="text-emerald-700 text-xs">✓ {d.extracted_text.length.toLocaleString()} chars</span>
                      : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`doc-del-${d.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- PROPERTY RESEARCH ---------------- */
function ResearchTab({ txn, reload }) {
  const [busy, setBusy] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);
  const facts = txn.facts || [];

  const research = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${txn.id}/research`);
      toast.success("Research complete — verify sources");
      await reload();
    } catch { toast.error("Research failed"); }
    finally { setBusy(false); }
  };

  const liveResearch = async () => {
    setLiveBusy(true);
    try {
      await api.post(`/transactions/${txn.id}/live-research`);
      toast.success("Live public records fetched");
      await reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Live research failed"); }
    finally { setLiveBusy(false); }
  };

  const confirm = async (fid) => {
    await api.post(`/transactions/${txn.id}/facts/${fid}/confirm`);
    toast.success("Fact confirmed");
    reload();
  };

  const classTone = {
    broker_confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
    extracted_document: "bg-blue-50 text-blue-800 border-blue-200",
    extracted_listing: "bg-blue-50 text-blue-800 border-blue-200",
    public_research: "bg-slate-50 text-slate-700 border-slate-200",
    ai_inference: "bg-amber-50 text-amber-800 border-amber-200",
    conflicting: "bg-red-50 text-red-800 border-red-200",
    missing: "bg-slate-100 text-slate-500 border-slate-200",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="kicker mb-2">Property Intelligence</div>
          <p className="text-sm text-slate-600 max-w-2xl">
            Every fact below shows source, confidence, and confirmation status.
            OfferForge does <span className="font-semibold text-navy">not</span> represent public research as a
            title commitment, appraisal, or legal opinion. Broker confirmation is required before use in a final document.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button className="btn-primary" onClick={liveResearch} disabled={liveBusy} data-testid="live-research-btn">
            {liveBusy ? <><Loader2 size={14} className="animate-spin" /> Fetching…</> : <><Globe size={14} /> Live Public Records</>}
          </button>
          <button className="btn-brass" onClick={research} disabled={busy} data-testid="research-btn">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Researching…</> : <><Search size={14} /> AI Research</>}
          </button>
        </div>
      </div>

      {facts.length === 0 ? (
        <div className="grid-panel p-10 text-sm text-slate-500 text-center">
          No facts yet. Extract from uploaded documents or run public research.
        </div>
      ) : (
        <div className="grid-panel">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-border sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2">Field</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Classification</th>
                <th className="px-4 py-2">Confidence</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {facts.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50" data-testid={`fact-row-${f.id}`}>
                  <td className="px-4 py-2 font-medium">{f.label}</td>
                  <td className="px-4 py-2 font-mono text-slate-700">{f.value || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-2 text-xs">
                    {f.source_name ? (
                      f.source_url
                        ? <a href={f.source_url} target="_blank" rel="noreferrer" className="link-brass">{f.source_name}</a>
                        : <span>{f.source_name}</span>
                    ) : <span className="text-slate-400">—</span>}
                    {f.conflicting_values?.length ? (
                      <div className="mt-1 text-red-700 flex items-center gap-1">
                        <AlertTriangle size={10} />
                        {f.conflicting_values.length} conflict{f.conflicting_values.length > 1 ? "s" : ""}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`badge-status ${classTone[f.classification] || classTone.public_research}`}>
                      {f.classification.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2 uppercase text-xs tracking-widest text-slate-600">{f.confidence}</td>
                  <td className="px-4 py-2 text-right">
                    {f.broker_confirmed
                      ? <span className="text-emerald-700 text-xs inline-flex items-center gap-1"><CheckCircle2 size={12} /> Confirmed</span>
                      : <button className="text-xs link-brass" onClick={() => confirm(f.id)} data-testid={`confirm-fact-${f.id}`}>Confirm</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- BUYER ---------------- */
function BuyerTab({ txn, patch }) {
  const [buyer, setBuyer] = useState(txn.buyer_data || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await patch({ buyer_data: buyer });
      toast.success("Buyer saved");
    } finally { setSaving(false); }
  };

  const FIELDS = [
    ["name", "Individual Buyer Name", "text"],
    ["buyer_entity", "Buyer Entity", "text"],
    ["entity_type", "Entity Type", "text"],
    ["state_of_formation", "State of Formation", "text"],
    ["authorized_signer", "Authorized Signer", "text"],
    ["signer_title", "Signer Title", "text"],
    ["mailing_address", "Mailing Address", "text"],
    ["email", "Email", "email"],
    ["phone", "Phone", "text"],
    ["intended_use", "Intended Property Use", "text"],
    ["existing_business", "Existing Business", "text"],
    ["business_experience", "Business Experience", "text"],
    ["financing_source", "Financing Source", "text"],
    ["proof_of_funds_status", "Proof of Funds", "text"],
    ["lender_status", "Lender Status", "text"],
    ["guarantor", "Guarantor", "text"],
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="kicker mb-2">Buyer / Client</div>
        <p className="text-sm text-slate-500">
          Never assume the entity, ownership, or signer. If uncertain, ask the client and confirm.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map(([k, l, t]) => (
          <div key={k}>
            <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{l}</label>
            <input type={t} value={buyer[k] || ""} onChange={(e) => setBuyer({ ...buyer, [k]: e.target.value })}
              data-testid={`buyer-${k}`}
              className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button className="btn-brass" onClick={save} disabled={saving} data-testid="buyer-save">
          {saving ? "Saving…" : "Save Buyer"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- OFFER TERMS ---------------- */
function OfferTab({ txn, patch }) {
  const [ot, setOT] = useState(txn.offer_terms || {});
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setOT({ ...ot, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await patch({ offer_terms: ot });
      toast.success("Offer terms saved");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <N label="Offer Price" v={ot.offer_price} on={(v) => upd("offer_price", v)} tid="ot-price" />
        <N label="Earnest Money" v={ot.earnest_money} on={(v) => upd("earnest_money", v)} tid="ot-em" />
        <T label="Earnest Money Due" v={ot.earnest_money_due} on={(v) => upd("earnest_money_due", v)} tid="ot-em-due" />
        <B label="Earnest Refundable" v={ot.earnest_money_refundable} on={(v) => upd("earnest_money_refundable", v)} tid="ot-em-refund" />
        <T label="Closing Date" v={ot.closing_date} on={(v) => upd("closing_date", v)} tid="ot-closing" type="date" />
        <N label="Feasibility Period (days)" v={ot.feasibility_period_days} on={(v) => upd("feasibility_period_days", v)} int tid="ot-feas" />
        <N label="Inspection Period (days)" v={ot.inspection_period_days} on={(v) => upd("inspection_period_days", v)} int tid="ot-insp" />
        <B label="Financing Contingency" v={ot.financing_contingency} on={(v) => upd("financing_contingency", v)} tid="ot-fin-cont" />
        <B label="Appraisal Contingency" v={ot.appraisal_contingency} on={(v) => upd("appraisal_contingency", v)} tid="ot-appraisal" />
        <N label="Title Review (days)" v={ot.title_review_days} on={(v) => upd("title_review_days", v)} int tid="ot-title-days" />
        <N label="Environmental Review (days)" v={ot.environmental_review_days} on={(v) => upd("environmental_review_days", v)} int tid="ot-env-days" />
        <B label="Survey Required" v={ot.survey_required} on={(v) => upd("survey_required", v)} tid="ot-survey" />
        <T label="Property Condition" v={ot.property_condition} on={(v) => upd("property_condition", v)} tid="ot-condition" />
        <T label="Included Personal Property" v={ot.included_personal_property} on={(v) => upd("included_personal_property", v)} tid="ot-personal" />
        <T label="Existing Tenants" v={ot.existing_tenants} on={(v) => upd("existing_tenants", v)} tid="ot-tenants" />
        <T label="Lease Assignments" v={ot.lease_assignments} on={(v) => upd("lease_assignments", v)} tid="ot-leases" />
        <T label="Escrow Company" v={ot.escrow_company} on={(v) => upd("escrow_company", v)} tid="ot-escrow" />
        <T label="Title Company" v={ot.title_company} on={(v) => upd("title_company", v)} tid="ot-title" />
        <T label="Closing Costs" v={ot.closing_costs} on={(v) => upd("closing_costs", v)} tid="ot-closing-costs" />
        <T label="Prorations" v={ot.prorations} on={(v) => upd("prorations", v)} tid="ot-prorations" />
        <T label="Possession" v={ot.possession} on={(v) => upd("possession", v)} tid="ot-possession" />
        <T label="Offer Expiration" v={ot.offer_expiration} on={(v) => upd("offer_expiration", v)} tid="ot-expiration" type="date" />
        <B label="Personal Guaranty" v={ot.personal_guaranty} on={(v) => upd("personal_guaranty", v)} tid="ot-pg" />
      </div>
      <div className="flex justify-end">
        <button className="btn-brass" onClick={save} disabled={saving} data-testid="offer-save">
          {saving ? "Saving…" : "Save Offer Terms"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- FINANCING ---------------- */
function FinancingTab({ txn, patch }) {
  const [ft, setFT] = useState(txn.financing_terms || {});
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState(null);
  const upd = (k, v) => setFT({ ...ft, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await patch({ financing_terms: ft });
      toast.success("Financing saved");
    } finally { setSaving(false); }
  };

  const compute = async () => {
    await save();
    const { data } = await api.post(`/transactions/${txn.id}/calculate`);
    setCalc(data.results);
    toast.success("Recalculated");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="kicker mb-2">Financing Terms</div>
          <p className="text-sm text-slate-500 max-w-lg">
            Every financing input feeds the calculator. Nothing is defaulted. Missing fields become required questions.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!ft.seller_financing} onChange={(e) => upd("seller_financing", e.target.checked)}
            data-testid="ft-seller-financing" className="accent-brass" />
          <span>Seller Financing</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <N label="Purchase Price" v={ft.purchase_price} on={(v) => upd("purchase_price", v)} tid="ft-price" />
        <N label="Down Payment" v={ft.down_payment} on={(v) => upd("down_payment", v)} tid="ft-down" />
        <N label="Financed Principal" v={ft.financed_principal} on={(v) => upd("financed_principal", v)} tid="ft-principal" />
        <N label="Interest Rate (%)" v={ft.interest_rate} on={(v) => upd("interest_rate", v)} tid="ft-rate" decimal />
        <N label="Payment Amount" v={ft.payment_amount} on={(v) => upd("payment_amount", v)} tid="ft-pmt" />
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Payment Frequency</label>
          <select value={ft.payment_frequency || ""} onChange={(e) => upd("payment_frequency", e.target.value || null)}
            data-testid="ft-freq"
            className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass">
            <option value="">—</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <N label="Interest-Only Period (mo)" v={ft.interest_only_period_months} on={(v) => upd("interest_only_period_months", v)} int tid="ft-io" />
        <N label="Amortization (years)" v={ft.amortization_years} on={(v) => upd("amortization_years", v)} int tid="ft-amort" />
        <N label="Annual Principal Payments" v={ft.annual_principal_payments} on={(v) => upd("annual_principal_payments", v)} tid="ft-annual-prin" />
        <T label="Balloon Date" v={ft.balloon_date} on={(v) => upd("balloon_date", v)} type="date" tid="ft-balloon-date" />
        <N label="Balloon Amount" v={ft.balloon_amount} on={(v) => upd("balloon_amount", v)} tid="ft-balloon-amt" />
        <T label="Prepayment Penalty" v={ft.prepayment_penalty} on={(v) => upd("prepayment_penalty", v)} tid="ft-prepay" />
        <N label="Default Rate (%)" v={ft.default_rate} on={(v) => upd("default_rate", v)} decimal tid="ft-default" />
        <T label="Late Fee" v={ft.late_fee} on={(v) => upd("late_fee", v)} tid="ft-late" />
        <T label="Security Instrument" v={ft.security_instrument} on={(v) => upd("security_instrument", v)} tid="ft-security" />
        <B label="Personal Guaranty" v={ft.personal_guaranty} on={(v) => upd("personal_guaranty", v)} tid="ft-pg" />
        <T label="Additional Collateral" v={ft.additional_collateral} on={(v) => upd("additional_collateral", v)} tid="ft-collateral" />
        <T label="Subordination Rights" v={ft.subordination_rights} on={(v) => upd("subordination_rights", v)} tid="ft-subord" />
        <T label="Existing Financing Payoff" v={ft.existing_financing_payoff} on={(v) => upd("existing_financing_payoff", v)} tid="ft-payoff" />
        <T label="Escrow Servicing" v={ft.escrow_servicing} on={(v) => upd("escrow_servicing", v)} tid="ft-servicing" />
        <T label="Taxes & Insurance" v={ft.taxes_and_insurance} on={(v) => upd("taxes_and_insurance", v)} tid="ft-ti" />
        <B label="Due on Sale" v={ft.due_on_sale} on={(v) => upd("due_on_sale", v)} tid="ft-dos" />
      </div>

      <div className="flex justify-end gap-3">
        <button className="btn-ghost" onClick={save} disabled={saving}>Save</button>
        <button className="btn-brass" onClick={compute} data-testid="ft-calculate">
          <RefreshCw size={14} /> Save & Recalculate
        </button>
      </div>

      {calc && (
        <div className="grid-panel p-5" data-testid="calc-panel">
          <div className="kicker mb-3">Calculations</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {Object.entries(calc).map(([k, v]) => (
                <tr key={k}>
                  <td className="py-2 text-slate-600">{k.replace(/_/g, " ")}</td>
                  <td className="py-2 mono-num text-navy">{typeof v === "number" ? v.toLocaleString() : String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- COMMISSION ---------------- */
function CommissionTab({ txn, patch, reload }) {
  const [ct, setCT] = useState(txn.commission_terms || {});
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setCT({ ...ct, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await patch({ commission_terms: ct }); toast.success("Commission saved"); }
    finally { setSaving(false); }
  };

  const recalc = async () => {
    await save();
    await api.post(`/transactions/${txn.id}/calculate`);
    await reload();
    toast.success("Recalculated");
  };

  const approve = async () => {
    await save();
    try {
      await api.post(`/transactions/${txn.id}/commission/approve`);
      toast.success("Commission approved");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    }
  };

  const price = txn.offer_terms?.offer_price || txn.financing_terms?.purchase_price || 0;
  const grossPreview = ct.commission_percentage && price ? price * (ct.commission_percentage / 100) : ct.gross_commission;
  const sidePreview = grossPreview && ct.cooperating_split_percentage ? grossPreview * (ct.cooperating_split_percentage / 100) : ct.broker_side_commission;

  return (
    <div className="space-y-6">
      <div className="bg-brass-50 border-l-4 border-brass p-4 rounded-sm">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-brass-700 shrink-0 mt-0.5" />
          <div className="text-sm text-navy-900">
            <div className="font-semibold mb-1">Commission Protection</div>
            OfferForge never assumes or defaults commission. All commission terms must be
            explicitly entered and approved by the broker before any offer document is generated.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <N label="Commission %" v={ct.commission_percentage} on={(v) => upd("commission_percentage", v)} decimal tid="ct-pct" />
        <N label="Cooperating Split %" v={ct.cooperating_split_percentage} on={(v) => upd("cooperating_split_percentage", v)} decimal tid="ct-coop" />
        <N label="Brokerage Split %" v={ct.brokerage_split_percentage} on={(v) => upd("brokerage_split_percentage", v)} decimal tid="ct-brokerage" />
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Paying Party</label>
          <select value={ct.paying_party || ""} onChange={(e) => upd("paying_party", e.target.value || null)}
            data-testid="ct-paying"
            className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass">
            <option value="">—</option>
            <option value="seller">Seller</option>
            <option value="buyer">Buyer</option>
            <option value="split">Split</option>
          </select>
        </div>
        <T label="Receiving Broker" v={ct.receiving_broker} on={(v) => upd("receiving_broker", v)} tid="ct-receiving" />
        <B label="Included in Offer" v={ct.included_in_offer} on={(v) => upd("included_in_offer", v)} tid="ct-included" />
      </div>

      <div className="grid-panel p-5">
        <div className="kicker mb-3">Approval Preview</div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            <tr><td className="py-2 text-slate-600">Purchase Price</td><td className="py-2 mono-num">{money(price)}</td></tr>
            <tr><td className="py-2 text-slate-600">Commission %</td><td className="py-2 mono-num">{ct.commission_percentage != null ? `${ct.commission_percentage}%` : "—"}</td></tr>
            <tr><td className="py-2 text-slate-600">Estimated Gross Commission</td><td className="py-2 mono-num text-brass-700">{money(grossPreview)}</td></tr>
            <tr><td className="py-2 text-slate-600">Cooperating Broker Split</td><td className="py-2 mono-num">{ct.cooperating_split_percentage != null ? `${ct.cooperating_split_percentage}%` : "—"}</td></tr>
            <tr><td className="py-2 text-slate-600">Estimated Broker-Side Commission</td><td className="py-2 mono-num text-brass-700">{money(sidePreview)}</td></tr>
            <tr><td className="py-2 text-slate-600">Responsible Paying Party</td><td className="py-2 mono-num uppercase">{ct.paying_party || "—"}</td></tr>
            <tr><td className="py-2 text-slate-600">Included in Offer</td><td className="py-2 mono-num">{ct.included_in_offer ? "Yes" : ct.included_in_offer === false ? "No" : "—"}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm">
          {ct.approved
            ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Approved by {ct.approved_by} on {ct.approved_at?.slice(0, 10)}</span>
            : <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle size={14} /> Approval required before drafting</span>}
        </div>
        <div className="flex gap-3">
          <button className="btn-ghost" onClick={save} disabled={saving}>Save</button>
          <button className="btn-primary" onClick={recalc} data-testid="ct-recalc"><RefreshCw size={14} /> Recalculate</button>
          <button className="btn-brass" onClick={approve} data-testid="ct-approve" disabled={ct.approved}>
            <ShieldCheck size={14} /> {ct.approved ? "Approved" : "Approve Commission"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- MISSING INFO ---------------- */
function MissingTab({ txn, reload }) {
  const [busy, setBusy] = useState(false);
  const questions = txn.questions || [];

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/transactions/${txn.id}/questions/generate`);
      toast.success("Questions generated");
      await reload();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const answer = async (qid, val) => {
    await api.post(`/transactions/${txn.id}/questions/${qid}/answer`, { answer: val });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="kicker mb-2">Missing Information Engine</div>
          <p className="text-sm text-slate-500 max-w-2xl">
            Targeted qualifying questions based on your transaction type. Nothing is assumed —
            fields you don&apos;t answer become placeholders in the draft.
          </p>
        </div>
        <button className="btn-brass" onClick={generate} disabled={busy} data-testid="gen-questions-btn">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Questions</>}
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="grid-panel p-10 text-sm text-slate-500 text-center">
          No questions yet. Generate to see what&apos;s missing.
        </div>
      ) : (
        <div className="grid-panel divide-y divide-border">
          {questions.map((q) => <QuestionItem key={q.id} q={q} onSave={(v) => answer(q.id, v)} />)}
        </div>
      )}
    </div>
  );
}

function QuestionItem({ q, onSave }) {
  const [val, setVal] = useState(q.answer || "");
  const [saved, setSaved] = useState(q.answered);
  const priorityTone = {
    required: "bg-red-50 text-red-800 border-red-200",
    recommended: "bg-amber-50 text-amber-800 border-amber-200",
    optional: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className="p-4" data-testid={`q-item-${q.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="text-sm font-medium text-navy">{q.text}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{q.category}</div>
        </div>
        <span className={`badge-status ${priorityTone[q.priority] || priorityTone.optional}`}>{q.priority}</span>
      </div>
      <div className="flex gap-2 mt-2">
        <input value={val} onChange={(e) => { setVal(e.target.value); setSaved(false); }}
          placeholder="Answer…"
          data-testid={`q-input-${q.id}`}
          className="flex-1 bg-white border border-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
        <button className="btn-primary text-xs px-3" onClick={async () => { await onSave(val); setSaved(true); toast.success("Saved"); }}
          data-testid={`q-save-${q.id}`}>
          {saved ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- READINESS ---------------- */
function ReadinessTab({ txn, reload }) {
  const [rd, setRD] = useState(txn.readiness_score);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/transactions/${txn.id}/readiness`);
      setRD(data);
      await reload();
    } finally { setBusy(false); }
  };

  useEffect(() => { if (!rd) refresh(); }, [txn.id]);

  const metrics = rd ? [
    ["property_data", "Property Data"], ["buyer_data", "Buyer Data"],
    ["offer_terms", "Offer Terms"], ["financing", "Financing"],
    ["verification", "Public-Record Verification"], ["commission", "Commission"],
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="kicker mb-2">Offer Readiness</div>
          <p className="text-sm text-slate-500">Every completeness check, updated live.</p>
        </div>
        <button className="btn-primary" onClick={refresh} disabled={busy} data-testid="readiness-refresh">
          <RefreshCw size={14} /> {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {rd && (
        <>
          <div className="grid-panel p-6 flex items-center gap-6">
            <div className="relative w-32 h-32 shrink-0" data-testid="readiness-ring">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" strokeWidth="10" stroke="#E2E8F0" fill="none" />
                <circle cx="50" cy="50" r="42" strokeWidth="10" stroke="#C5A059" fill="none"
                  strokeDasharray={`${(rd.overall / 100) * 264} 264`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-mono text-3xl text-navy">{rd.overall}%</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall</div>
              </div>
            </div>
            <div>
              <div className="kicker text-brass mb-1">Status</div>
              <div className="text-2xl font-display capitalize">{rd.status?.replace(/_/g, " ")}</div>
              <div className="text-sm text-slate-500 mt-2 max-w-xl">
                {rd.overall >= 90 ? "All checks passed. Safe to draft the offer package." :
                 rd.overall >= 75 ? "Most terms in place. Broker review recommended before drafting." :
                 rd.overall >= 50 ? "Verification and confirmations still needed." :
                 "Substantial missing information. Complete required fields before proceeding."}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {metrics.map(([k, l]) => (
              <div key={k} className="grid-panel p-4" data-testid={`metric-${k}`}>
                <div className="kicker">{l}</div>
                <div className="mt-2 font-mono text-2xl text-navy">{rd[k]}%</div>
                <div className="mt-2 h-1 bg-slate-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-brass" style={{ width: `${rd[k]}%` }}></div>
                </div>
              </div>
            ))}
          </div>

          {rd.outstanding?.length > 0 && (
            <div className="grid-panel p-5">
              <div className="kicker mb-3 text-amber-800">Outstanding</div>
              <ul className="space-y-2 text-sm">
                {rd.outstanding.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700">
                    <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- DOCUMENT GENERATION ---------------- */
function DocumentTab({ txn, reload }) {
  const [docKind, setDocKind] = useState("loi");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [busy, setBusy] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/templates");
      setTemplates(data);
    })();
  }, []);

  const generate = async () => {
    if (!txn.commission_terms?.approved) {
      toast.error("Commission must be approved before generating an offer");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/transactions/${txn.id}/generate`, {
        doc_kind: docKind, template_id: templateId || null,
      });
      setMarkdown(data.markdown);
      toast.success("Draft generated");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/transactions/${txn.id}/download?fmt=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `offer-${txn.id}.${fmt}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Generate the document first");
    }
  };

  const approveFinal = async () => {
    if (!confirm("Mark this document as Final? This is the broker's explicit approval.")) return;
    setFinalizing(true);
    try {
      await api.post(`/transactions/${txn.id}/approve-final`);
      toast.success("Marked as Final");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setFinalizing(false); }
  };

  const DOC_KINDS = [
    ["loi", "Letter of Intent"], ["offer", "Purchase Offer Letter"],
    ["seller_financed", "Seller-Financed Proposal"], ["buyer_intro", "Buyer Introduction Letter"],
    ["summary", "Offer Summary"], ["submission_email", "Broker Submission Email"],
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Document Kind</label>
          <select value={docKind} onChange={(e) => setDocKind(e.target.value)}
            data-testid="doc-kind-select"
            className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass">
            {DOC_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">Template</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
            data-testid="doc-template-select"
            className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass">
            <option value="">Default OfferForge</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button className="btn-brass w-full justify-center" onClick={generate} disabled={busy}
            data-testid="generate-doc-btn">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Drafting…</> : <><Sparkles size={14} /> Generate Draft</>}
          </button>
        </div>
      </div>

      {!txn.commission_terms?.approved && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-sm text-sm text-red-900 flex items-start gap-3">
          <XCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Commission approval required</div>
            Approve commission terms before drafting any offer document.
          </div>
        </div>
      )}

      {markdown && (
        <div className="grid-panel">
          <div className="border-b border-border px-5 py-3 flex items-center justify-between">
            <div className="kicker">Draft Preview</div>
            <div className="flex gap-2">
              <button className="btn-ghost text-xs" onClick={() => download("docx")} data-testid="dl-docx">
                <Download size={12} /> DOCX
              </button>
              <button className="btn-ghost text-xs" onClick={() => download("pdf")} data-testid="dl-pdf">
                <Download size={12} /> PDF
              </button>
              <button className="btn-brass text-xs" onClick={approveFinal} disabled={finalizing || txn.status === "final"}
                data-testid="approve-final-btn">
                <ShieldCheck size={12} /> {txn.status === "final" ? "Final" : "Approve as Final"}
              </button>
            </div>
          </div>
          <div className="p-8 max-h-[70vh] overflow-y-auto bg-white">
            <MarkdownView md={markdown} />
          </div>
        </div>
      )}

      {!markdown && txn.generated_document_path && (
        <div className="grid-panel p-5 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            A document has been generated. Regenerate or download the existing version.
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => download("docx")}><Download size={14} /> DOCX</button>
            <button className="btn-brass" onClick={() => download("pdf")}><Download size={14} /> PDF</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkdownView({ md }) {
  // Very light markdown renderer
  const lines = md.split("\n");
  const elements = [];
  lines.forEach((line, i) => {
    if (!line.trim()) { elements.push(<div key={i} className="h-3" />); return; }
    if (line.startsWith("## ")) elements.push(<h2 key={i} className="font-display text-2xl text-brass-700 mt-6 mb-2">{line.slice(3)}</h2>);
    else if (line.startsWith("### ")) elements.push(<h3 key={i} className="font-semibold text-navy mt-4 mb-1">{line.slice(4)}</h3>);
    else if (line.startsWith("# ")) elements.push(<h1 key={i} className="font-display text-3xl text-navy mt-6 mb-3">{line.slice(2)}</h1>);
    else if (line.startsWith("- ") || line.startsWith("* ")) elements.push(<li key={i} className="ml-6 list-disc text-slate-800">{renderInline(line.slice(2))}</li>);
    else elements.push(<p key={i} className="text-slate-800 leading-relaxed">{renderInline(line)}</p>);
  });
  return <div className="prose max-w-none">{elements}</div>;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
    return <span key={i}>{p}</span>;
  });
}

/* ---------- Small field components ---------- */
function T({ label, v, on, tid, type = "text" }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{label}</label>
      <input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brass" />
    </div>
  );
}
function N({ label, v, on, tid, int = false, decimal = false }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-slate-600 mb-1.5">{label}</label>
      <input
        type="number" step={decimal ? "0.01" : int ? "1" : "any"}
        value={v === null || v === undefined ? "" : v}
        onChange={(e) => on(e.target.value === "" ? null : Number(e.target.value))}
        data-testid={tid}
        className="w-full bg-white border border-border px-3 py-2.5 text-sm rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-brass"
      />
    </div>
  );
}
function B({ label, v, on, tid }) {
  return (
    <div className="flex items-center gap-2 pt-6">
      <input type="checkbox" checked={!!v} onChange={(e) => on(e.target.checked)}
        data-testid={tid} className="accent-brass" />
      <label className="text-sm text-slate-700">{label}</label>
    </div>
  );
}
